<script lang="ts" setup>
import { ref, computed, inject, watch, onMounted } from "vue"
import axios from "axios"
import { useChatbotStore } from "@/store/useChatbotStore"
import { ClaudeService } from "@/services/ClaudeService"
import type { ClaudeActionResponse } from "@/services/ClaudeService"
import type { ActionDTO, ActionInput } from "@/types/gen"

interface RunPageMethods {
    getScreenshotBase64: () => Promise<{ base64: string, width: number, height: number }>
    doCreateAction: (type: string, inputType?: string, inputText?: string) => Promise<ActionDTO>
    doSaveAction: (action: ActionDTO, noReplay?: boolean) => Promise<void>
    playCurrentAction: () => Promise<void>
    createPointerActionWithAnchor: (x: number, y: number, inputType: string) => Promise<void>
    device: { value: { deviceStatus: { playing: boolean; imageUrl: string } } }
}

const runPageMethods = inject<RunPageMethods>("runPageMethods")

const chatbotStore = useChatbotStore()

const apiKey = ref(localStorage.getItem("claude-api-key") || "")
const adminManagedKey = ref(false)
const adminAiModel = ref("")
const adminSystemPrompt = ref("")
const adminUserPrompt = ref("")
const showSettings = ref(!apiKey.value)
const apiKeyInput = ref("")

// Try to fetch admin-managed Claude AI settings
onMounted(async () => {
    try {
        const token = localStorage.getItem("_idToken")
        if (token) {
            const res = await axios.get("/testup-auth/auth/ai-settings", {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.data.aiModel) adminAiModel.value = res.data.aiModel
            if (res.data.aiSystemPrompt) adminSystemPrompt.value = res.data.aiSystemPrompt
            if (res.data.aiUserPrompt) adminUserPrompt.value = res.data.aiUserPrompt
            if (res.data.claudeApiKey) {
                apiKey.value = res.data.claudeApiKey
                adminManagedKey.value = true
                showSettings.value = false
            }
        }
    } catch (e) {
        // Admin settings not available, fall back to defaults
    }
})
const useCaseInput = ref("")
const loopDetectionCount = ref(0)
const lastActionSignature = ref("")
const abortController = ref<AbortController | null>(null)

const statusClass = computed(() => {
    return {
        idle: "status-idle",
        analyzing: "status-analyzing",
        executing: "status-executing",
        paused: "status-paused",
        complete: "status-complete",
        error: "status-error"
    }[chatbotStore.status]
})

const statusText = computed(() => {
    return {
        idle: "Ready",
        analyzing: "Analyzing...",
        executing: "Executing...",
        paused: "Paused",
        complete: "Complete",
        error: "Error"
    }[chatbotStore.status]
})

function togglePanel() {
    chatbotStore.togglePanel()
}

function saveApiKey() {
    if (apiKeyInput.value.trim()) {
        localStorage.setItem("claude-api-key", apiKeyInput.value.trim())
        apiKey.value = apiKeyInput.value.trim()
        showSettings.value = false
    }
}

function openSettings() {
    apiKeyInput.value = apiKey.value
    showSettings.value = true
}

function cancelSettings() {
    showSettings.value = !apiKey.value
}

async function startRecording() {
    if (!useCaseInput.value.trim() || !apiKey.value) return

    chatbotStore.reset()
    chatbotStore.useCase = useCaseInput.value.trim()
    chatbotStore.addMessage("user", chatbotStore.useCase)
    loopDetectionCount.value = 0
    lastActionSignature.value = ""

    await runChatbotLoop()
}

function pauseRecording() {
    chatbotStore.setStatus("paused")
}

function resumeRecording() {
    chatbotStore.clearStopRequest()
    chatbotStore.setStatus("analyzing")
    runChatbotLoop()
}

function stopRecording() {
    chatbotStore.requestStop()
    // Abort any pending API request
    if (abortController.value) {
        abortController.value.abort()
        abortController.value = null
    }
    chatbotStore.setStatus("idle")
    chatbotStore.addMessage("system", "Recording stopped by user")
}

async function runChatbotLoop() {
    if (!runPageMethods) {
        chatbotStore.setStatus("error")
        chatbotStore.addMessage("system", "Error: RunPage methods not available")
        return
    }

    const claudeService = new ClaudeService(
        apiKey.value,
        adminAiModel.value || undefined,
        adminSystemPrompt.value || undefined,
        adminUserPrompt.value || undefined
    )

    while (chatbotStore.status !== "complete" && chatbotStore.status !== "error" && !chatbotStore.shouldStop) {
        if (chatbotStore.status === "paused") {
            return
        }

        try {
            chatbotStore.setStatus("analyzing")

            if (!await waitForDeviceIdle()) {
                break // Stop requested
            }

            if (chatbotStore.shouldStop) break

            const { base64: screenshot, width: imgWidth, height: imgHeight } = await runPageMethods.getScreenshotBase64()
            console.log(`[AI Recorder] Screenshot captured: ${imgWidth}x${imgHeight}`)

            if (chatbotStore.shouldStop) break

            // Create abort controller for this API call
            abortController.value = new AbortController()

            let response
            try {
                response = await claudeService.analyzeScreenAndGetAction(
                    screenshot,
                    chatbotStore.useCase,
                    chatbotStore.executedActions,
                    imgWidth,
                    imgHeight,
                    abortController.value.signal
                )
            } catch (error: any) {
                if (error.name === "AbortError" || chatbotStore.shouldStop) {
                    break // Stop requested
                }
                throw error
            } finally {
                abortController.value = null
            }

            if (chatbotStore.shouldStop) break

            if (response.isComplete) {
                chatbotStore.setStatus("complete")
                chatbotStore.addMessage("assistant", response.explanation || "Use case complete! All actions recorded.")
                break
            }

            // If no action but not complete, Claude might have requested a screenshot
            // Continue the loop to try again with a fresh screenshot
            if (!response.action) {
                console.log("[AI Recorder] No action returned, retrying with fresh screenshot...")
                chatbotStore.addMessage("system", "Retrying...")
                if (!await sleep(1000)) break
                continue
            }

            const actionSignature = JSON.stringify(response.action)
            if (actionSignature === lastActionSignature.value) {
                loopDetectionCount.value++
                if (loopDetectionCount.value >= 3) {
                    chatbotStore.setStatus("paused")
                    chatbotStore.addMessage("system", "Loop detected: same action suggested 3 times. Please review and resume or stop.")
                    break
                }
            } else {
                loopDetectionCount.value = 0
                lastActionSignature.value = actionSignature
            }

            chatbotStore.addMessage("assistant", response.explanation, {
                type: response.action.type,
                inputType: response.action.inputType
            })

            chatbotStore.setStatus("executing")

            if (chatbotStore.shouldStop) break

            // Remember the current screenshot URL before executing
            const previousImageUrl = runPageMethods.device.value.deviceStatus.imageUrl

            await createAndExecuteAction(response)

            if (chatbotStore.shouldStop) break

            chatbotStore.addExecutedAction(response.explanation)

            // Wait for a new screenshot to appear (up to 10 seconds)
            const maxWaitForNewScreenshot = 10000
            const startTime = Date.now()
            while (Date.now() - startTime < maxWaitForNewScreenshot) {
                if (chatbotStore.shouldStop) break
                if (!await sleep(500)) break
                const currentImageUrl = runPageMethods.device.value.deviceStatus.imageUrl
                if (currentImageUrl !== previousImageUrl) {
                    console.log("[AI Recorder] New screenshot detected")
                    // Wait a bit more for the image to fully load
                    if (!await sleep(1000)) break
                    break
                }
            }

            if (chatbotStore.shouldStop) break

            // Additional wait to ensure screen has settled
            if (!await sleep(1000)) break

        } catch (error: any) {
            if (chatbotStore.shouldStop) break
            chatbotStore.setStatus("error")
            chatbotStore.addMessage("system", `Error: ${error.message}`)
            break
        }
    }

    if (chatbotStore.shouldStop) {
        chatbotStore.clearStopRequest()
    }
}

async function createAndExecuteAction(response: ClaudeActionResponse) {
    if (!runPageMethods || !response.action) return

    const { type, inputType, text, x, y } = response.action

    console.log(`[AI Recorder] Claude response - type: ${type}, inputType: ${inputType}, x: ${x}, y: ${y}, text: ${text}`)

    // Create a TAG action with the LLM explanation before the actual action
    const tagText = `AI: ${response.explanation}`
    await runPageMethods.doCreateAction("MARKER", "", tagText)
    // Play the TAG action to advance the current position before creating the next action
    await runPageMethods.playCurrentAction()

    if (type === "POINTER") {
        // POINTER actions need an anchor area - use the dedicated method
        // x, y are now pixel coordinates from Claude's computer-use tool
        await runPageMethods.createPointerActionWithAnchor(x ?? 960, y ?? 540, inputType)
    } else if (type === "TEXT" || type === "CONTROL") {
        // Both TEXT and CONTROL actions (KEY, SCROLL, ENTER, etc.) use "TEXT" action type
        // with a simple {type, text} input structure
        const action = await runPageMethods.doCreateAction("TEXT")
        action.inputs = [{
            type: inputType,
            text: text ?? ""
        } as ActionInput]
        await runPageMethods.doSaveAction(action)
        await runPageMethods.playCurrentAction()
    }
}

async function waitForDeviceIdle(): Promise<boolean> {
    if (!runPageMethods) return true

    const maxWait = 30000
    const startTime = Date.now()

    while (runPageMethods.device.value.deviceStatus.playing) {
        if (chatbotStore.shouldStop) {
            return false // Interrupted
        }
        if (Date.now() - startTime > maxWait) {
            throw new Error("Timeout waiting for device to become idle")
        }
        if (!await sleep(200)) {
            return false // Interrupted
        }
    }
    return true
}

// Interruptible sleep that checks for stop request every 100ms
async function sleep(ms: number): Promise<boolean> {
    const interval = 100
    let elapsed = 0
    while (elapsed < ms) {
        if (chatbotStore.shouldStop) {
            return false // Interrupted
        }
        await new Promise(resolve => setTimeout(resolve, Math.min(interval, ms - elapsed)))
        elapsed += interval
    }
    return true // Completed normally
}

watch(() => chatbotStore.isOpen, (isOpen) => {
    if (isOpen && !apiKey.value) {
        showSettings.value = true
    }
})
</script>

<template lang="pug">
.chatbot-panel(:class="{ collapsed: !chatbotStore.isOpen }")
    .chatbot-toggle(@click="togglePanel")
        i.fas(:class="chatbotStore.isOpen ? 'fa-chevron-left' : 'fa-robot'")

    .chatbot-content(v-if="chatbotStore.isOpen")
        .chatbot-header
            h3 AI Test Recorder
            .header-right
                .status-badge(:class="statusClass") {{ statusText }}
                button.btn-settings(@click="openSettings" title="Settings")
                    i.fas.fa-cog

        template(v-if="showSettings")
            .chatbot-settings
                h4 API Settings
                template(v-if="adminManagedKey")
                    .admin-key-notice
                        i.fas.fa-check-circle(style="color:var(--c-primary)")
                        span  Claude API key is managed by your administrator
                    .settings-buttons
                        button.btn.btn-secondary(@click="cancelSettings") Close
                template(v-else)
                    .form-group
                        label Claude API Key
                        input.form-control(
                            type="password"
                            v-model="apiKeyInput"
                            placeholder="Enter your Claude API key"
                            @keydown.stop
                        )
                        small.text-muted Get your key from console.anthropic.com
                    .settings-buttons
                        button.btn.btn-secondary(@click="cancelSettings" v-if="apiKey") Cancel
                        button.btn.btn-primary(@click="saveApiKey" :disabled="!apiKeyInput.trim()") Save

        template(v-else)
            .chatbot-messages
                .message(
                    v-for="msg in chatbotStore.messages"
                    :key="msg.id"
                    :class="msg.role"
                )
                    .message-content {{ msg.content }}
                    .message-action(v-if="msg.action")
                        span.action-badge {{ msg.action.type }}
                        span.action-badge {{ msg.action.inputType }}

            .chatbot-input(v-if="chatbotStore.status === 'idle'")
                textarea(
                    v-model="useCaseInput"
                    placeholder="Describe what you want to test..."
                    rows="3"
                    @keydown.stop
                )
                button.btn.btn-primary(
                    @click="startRecording"
                    :disabled="!useCaseInput.trim() || !apiKey"
                )
                    i.fas.fa-play
                    span Start Recording

            .chatbot-controls(v-else)
                button.btn.btn-secondary(
                    @click="pauseRecording"
                    v-if="chatbotStore.isRunning"
                )
                    i.fas.fa-pause
                    span Pause

                button.btn.btn-primary(
                    @click="resumeRecording"
                    v-if="chatbotStore.status === 'paused'"
                )
                    i.fas.fa-play
                    span Resume

                button.btn.btn-danger(
                    @click="stopRecording"
                    v-if="chatbotStore.status !== 'idle' && chatbotStore.status !== 'complete'"
                )
                    i.fas.fa-stop
                    span Stop

                button.btn.btn-secondary(
                    @click="chatbotStore.reset()"
                    v-if="chatbotStore.status === 'complete' || chatbotStore.status === 'error'"
                )
                    i.fas.fa-redo
                    span New Recording
</template>

<style lang="css" scoped>
.chatbot-panel {
    height: 100vh;
    width: 350px;
    min-width: 350px;
    background: white;
    border-right: 2px solid var(--bright-green);
    transition: width 0.3s ease, min-width 0.3s ease;
    display: flex;
    flex-direction: column;
    position: relative;
}

.chatbot-panel.collapsed {
    width: 0;
    min-width: 0;
    border-right: none;
    overflow: hidden;
}

.chatbot-panel.collapsed .chatbot-content {
    display: none;
}

.chatbot-toggle {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 60px;
    background: var(--bright-green);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border-radius: 0 4px 4px 0;
    font-size: 18px;
    z-index: 10;
}

.chatbot-panel.collapsed .chatbot-toggle {
    right: 0;
    border-radius: 0;
}

.chatbot-toggle:hover {
    background: var(--green);
}

.chatbot-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

.chatbot-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid var(--tu-border-lightgray);
    background: var(--very-light-grey);
}

.chatbot-header h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.status-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.7rem;
    text-transform: uppercase;
    font-weight: 600;
}

.status-idle { background: var(--light-grey); color: var(--black); }
.status-analyzing { background: var(--bright-orange); color: white; }
.status-executing { background: var(--bright-green); color: white; }
.status-complete { background: var(--green); color: white; }
.status-error { background: var(--red); color: white; }
.status-paused { background: var(--dark-grey); color: white; }

.btn-settings {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    color: var(--dark-grey);
}

.btn-settings:hover {
    color: var(--black);
}

.chatbot-settings {
    padding: 1rem;
    flex: 1;
}

.chatbot-settings h4 {
    margin: 0 0 1rem 0;
    font-size: 0.9rem;
}

.chatbot-settings .form-group {
    margin-bottom: 1rem;
}

.chatbot-settings label {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.85rem;
    font-weight: 500;
}

.chatbot-settings .form-control {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid var(--tu-border-lightgray);
    border-radius: 4px;
    font-size: 0.85rem;
}

.chatbot-settings .text-muted {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: var(--dark-grey);
}

.admin-key-notice {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: #dcfce7;
    border-radius: 0.5rem;
    font-size: 0.85rem;
    color: #166534;
    margin-bottom: 0.75rem;
}

.settings-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
}

.chatbot-messages {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
}

.message {
    margin-bottom: 1rem;
    padding: 0.75rem;
    border-radius: 4px;
    font-size: 0.85rem;
}

.message.user {
    background: var(--very-light-grey);
}

.message.assistant {
    background: rgba(148, 217, 4, 0.1);
    border-left: 3px solid var(--bright-green);
}

.message.system {
    background: rgba(253, 164, 4, 0.1);
    border-left: 3px solid var(--bright-orange);
}

.message-content {
    line-height: 1.4;
}

.message-action {
    margin-top: 0.5rem;
    display: flex;
    gap: 0.25rem;
}

.action-badge {
    padding: 0.15rem 0.4rem;
    background: var(--light-grey);
    border-radius: 3px;
    font-size: 0.7rem;
    font-weight: 500;
}

.chatbot-input {
    padding: 1rem;
    border-top: 1px solid var(--tu-border-lightgray);
}

.chatbot-input textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid var(--tu-border-lightgray);
    border-radius: 4px;
    font-size: 0.85rem;
    resize: none;
    margin-bottom: 0.5rem;
}

.chatbot-input textarea:focus {
    outline: none;
    border-color: var(--bright-green);
}

.chatbot-input .btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.chatbot-controls {
    padding: 1rem;
    border-top: 1px solid var(--tu-border-lightgray);
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.chatbot-controls .btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-width: 100px;
}

.btn {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 500;
}

.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-primary {
    background: var(--c-primary);
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background: var(--c-primary-dark);
}

.btn-secondary {
    background: var(--light-grey);
    color: var(--black);
}

.btn-secondary:hover:not(:disabled) {
    background: var(--dark-grey);
    color: white;
}

.btn-danger {
    background: var(--red);
    color: white;
}

.btn-danger:hover:not(:disabled) {
    background: var(--dark-red);
}
</style>
